import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Board } from '@/api';
import { errorMessage } from '@/api/client';
import { useUiStore } from '@/stores/ui';

/* Import di immagini trascinate dal Finder: file sciolti o cartelle intere.
 *
 * Tre pezzi, usati insieme da PinsView (drop dentro una board) e da BoardGrid
 * (drop sull'elenco: ogni cartella diventa una board):
 *
 * - `leggiDrop` traduce il DataTransfer del drop in immagini e cartelle,
 *   scendendo ricorsivamente nelle sottocartelle;
 * - `useImportCartella().importa` manda le immagini all'endpoint
 *   `boards/import-folder/` a lotti, raccontando il progresso in un toast;
 * - `useZonaDrop` gestisce l'evidenziazione della zona di rilascio.
 */

/* Riconoscimento per MIME quando c'è, per estensione quando manca (i file
 * letti tramite le entry di una cartella a volte arrivano senza type). */
const ESTENSIONE_IMMAGINE = /\.(jpe?g|png|gif|webp|bmp|tiff?|ico|heic|heif|avif)$/i;

function eImmagine(file) {
  if (!file || file.name.startsWith('.')) return false; // .DS_Store e parenti
  if (file.type) return file.type.startsWith('image/');
  return ESTENSIONE_IMMAGINE.test(file.name);
}

function fileDaEntry(entry) {
  // Un file illeggibile (permessi, alias rotto) si salta, non ferma il resto.
  return new Promise((resolve) => entry.file(resolve, () => resolve(null)));
}

async function vociDirectory(entry) {
  const reader = entry.createReader();
  const voci = [];
  // readEntries consegna a blocchi (WebKit: 100 alla volta): si insiste
  // finché non risponde vuoto, altrimenti una cartella grande arriva a metà.
  for (;;) {
    const blocco = await new Promise((resolve) =>
      reader.readEntries(resolve, () => resolve([])),
    );
    if (!blocco.length) break;
    voci.push(...blocco);
  }
  return voci;
}

/** Tutte le immagini dentro una entry, sottocartelle comprese. */
async function immaginiDentro(entry) {
  if (entry.name.startsWith('.')) return [];
  if (entry.isFile) {
    const file = await fileDaEntry(entry);
    return eImmagine(file) ? [file] : [];
  }
  if (entry.isDirectory) {
    const gruppi = await Promise.all((await vociDirectory(entry)).map(immaginiDentro));
    return gruppi.flat();
  }
  return [];
}

/**
 * Legge il contenuto di un drop: `{ file, cartelle }`, dove `file` sono le
 * immagini sciolte al primo livello e `cartelle` è un elenco
 * `{ nome, file }` — una voce per cartella rilasciata, con dentro tutte le
 * sue immagini, ricorsivamente.
 */
export async function leggiDrop(dataTransfer) {
  /* Le entry vanno estratte TUTTE prima del primo await: il DataTransfer
   * vive solo dentro l'evento di drop, subito dopo si svuota. */
  const voci = [];
  const sciolti = [];
  for (const item of dataTransfer?.items ?? []) {
    if (item.kind !== 'file') continue;
    // Con le entry (WebKit/Chrome) si distinguono le cartelle; senza, resta
    // il file piatto — niente cartelle, ma le immagini passano comunque.
    const entry = item.webkitGetAsEntry ? item.webkitGetAsEntry() : null;
    if (entry) voci.push(entry);
    else sciolti.push(item.getAsFile?.());
  }

  const file = sciolti.filter(eImmagine);
  const cartelle = [];
  for (const entry of voci) {
    if (entry.isDirectory) {
      cartelle.push({ nome: entry.name, file: await immaginiDentro(entry) });
    } else {
      const singolo = await fileDaEntry(entry);
      if (eImmagine(singolo)) file.push(singolo);
    }
  }
  return { file, cartelle };
}

/* L'endpoint accetta fino a 50 file per chiamata, ma i lotti stanno molto
 * sotto: richieste più piccole danno un progresso leggibile e, se una
 * fallisce, si perde poco. Il tetto in byte evita che otto foto da 20 MB
 * diventino una richiesta da 160.
 *
 * Il tetto DEVE stare sotto il `client_max_body_size` di nginx (vedi
 * deploy/nginx.conf, ora 512m). Quando erano 32 MB contro 30m un lotto pieno
 * veniva respinto con 413 e la richiesta troncata: al browser non arrivava
 * alcuna risposta leggibile e l'import moriva con un generico "Network
 * Error", indistinguibile da un guasto di rete.
 * `core/tests/test_deploy_config.py` inchioda il rapporto fra i due numeri. */
const LOTTO_FILE = 8;
const LOTTO_BYTE = 32 * 1024 * 1024;

/* Quanto può pesare al massimo UNA richiesta, tenendosi sotto il tetto di
 * nginx. Un file più grande di un lotto non si può spezzare: parte da solo,
 * ed è questo il limite che deve rispettare. Oltre, è inutile provarci. */
const TETTO_RICHIESTA = 480 * 1024 * 1024;

function aLotti(file) {
  const lotti = [];
  let corrente = [];
  let peso = 0;
  for (const f of file) {
    if (corrente.length && (corrente.length >= LOTTO_FILE || peso + f.size > LOTTO_BYTE)) {
      lotti.push(corrente);
      corrente = [];
      peso = 0;
    }
    corrente.push(f);
    peso += f.size;
  }
  if (corrente.length) lotti.push(corrente);
  return lotti;
}

/* Un file più pesante di quanto una singola richiesta possa portare non
 * entrerebbe in nessun lotto: partirebbe da solo e verrebbe respinto dal
 * proxy, facendo fallire tutto l'import invece che se stesso. Si scarta prima
 * di partire, e finisce nel conteggio degli scartati come qualunque altro
 * file che non si è potuto archiviare. Con il tetto a mezzo giga è un caso
 * che in pratica non capita: resta perché la modalità di guasto — un errore
 * di rete al posto di un motivo — era illeggibile. */
function separaTroppoGrandi(file) {
  const buoni = [];
  const troppoGrandi = [];
  for (const f of file) {
    (f.size > TETTO_RICHIESTA ? troppoGrandi : buoni).push(f);
  }
  return { buoni, troppoGrandi };
}

export function useImportCartella() {
  const ui = useUiStore();
  const { t } = useI18n();

  /**
   * Importa `file` nella board indicata — per id se esiste già, altrimenti
   * per nome (l'endpoint la crea o la ritrova, ignorando le maiuscole).
   * Un toast persistente racconta il progresso lotto per lotto; alla fine
   * un secondo toast tira le somme. Restituisce l'esito, o null se fallito.
   */
  async function importa({ boardId = null, nome, file }) {
    const { buoni, troppoGrandi } = separaTroppoGrandi(file);
    const esito = {
      board: null,
      aggiunte: 0,
      esistenti: 0,
      scartate: troppoGrandi.map((f) => ({ file: f.name, error: 'troppo grande' })),
    };
    const progresso = ui.notify(
      t('importProgress', { done: 0, total: file.length, board: nome }),
      'info',
      0, // persistente: lo si congeda a mano alla fine
    );
    try {
      for (const lotto of aLotti(buoni)) {
        const dati = await Board.importFolder({
          // Dal secondo lotto la board esiste di sicuro: si passa al suo id,
          // così un'eventuale rinomina a import in corso non sdoppia nulla.
          boardId: esito.board?.id ?? boardId,
          name: esito.board ? null : nome,
          files: lotto,
        });
        esito.board = dati.board;
        esito.aggiunte += dati.added;
        esito.esistenti += dati.existing;
        esito.scartate.push(...dati.errors);
        const fatte = esito.aggiunte + esito.esistenti + esito.scartate.length;
        ui.update(
          progresso,
          t('importProgress', { done: fatte, total: file.length, board: dati.board.name }),
        );
      }
      const parti = [t('importDoneAdded', { n: esito.aggiunte })];
      if (esito.esistenti) parti.push(t('importDoneExisting', { n: esito.esistenti }));
      if (esito.scartate.length) parti.push(t('importDoneFailed', { n: esito.scartate.length }));
      // Se non è partita nessuna richiesta (tutti i file scartati) la board
      // non è stata toccata: si nomina quella chiesta, non `null`.
      ui.notify(`«${esito.board?.name ?? nome}» — ${parti.join(' · ')}`);
      return esito;
    } catch (errore) {
      ui.notifyError(errorMessage(errore, t('importFailed')));
      return null;
    } finally {
      ui.dismiss(progresso);
    }
  }

  return { importa };
}

export function conFile(evento) {
  return Array.from(evento.dataTransfer?.types ?? []).includes('Files');
}

/* Tipo dedicato per il trascinamento di un pin dell'app (dalla griglia a una
 * board). Un tipo tutto suo — non 'text/plain' — così un pin nostro non si
 * confonde né con un file del Finder né con un'immagine trascinata dal web. */
export const TIPO_PIN = 'application/x-bacheca-pin';

export function conPin(evento) {
  return Array.from(evento.dataTransfer?.types ?? []).includes(TIPO_PIN);
}

/* Un'immagine trascinata da una pagina web (un'altra scheda, o il feed
 * Pinterest, che è web anch'esso). Porta l'URL come uri-list — a volte solo
 * dentro l'HTML del frammento. NON è un file (quello ha 'Files') né un pin
 * nostro (quello ha il tipo dedicato): quei due hanno la precedenza. */
export function conImmagineWeb(evento) {
  const tipi = Array.from(evento.dataTransfer?.types ?? []);
  if (tipi.includes(TIPO_PIN) || tipi.includes('Files')) return false;
  return tipi.includes('text/uri-list') || tipi.includes('text/html');
}

/** L'URL http(s) di un'immagine da un drop web, o null se non se ne trova. */
export function urlImmagine(dataTransfer) {
  for (const grezzo of [
    dataTransfer.getData('text/uri-list'),
    dataTransfer.getData('text/plain'),
  ]) {
    // uri-list può avere righe di commento che iniziano con '#': si prende la
    // prima riga che è davvero un URL.
    const riga = (grezzo || '')
      .split('\n')
      .map((s) => s.trim())
      .find((s) => /^https?:\/\//i.test(s));
    if (riga) return riga;
  }
  // Ultimo tentativo: il src dell'<img> nel frammento HTML trascinato.
  const html = dataTransfer.getData('text/html');
  const m = html && html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return m && /^https?:\/\//i.test(m[1]) ? m[1] : null;
}

/* Una zona "precisa" — una singola board nella colonna — ha il trascinamento
 * sopra di sé. La zona di pagina si fa da parte finché dura: fra due bersagli
 * sovrapposti vince il più piccolo, come in qualunque gestore di file. */
const zonaPrecisa = ref(false);

export function segnalaZonaPrecisa(dentro) {
  zonaPrecisa.value = dentro;
}

/**
 * Zona di rilascio grande quanto la FINESTRA. `attiva` dice se mostrare
 * l'invito, `gestisci(dataTransfer)` viene chiamata al rilascio; `abilitata`
 * (ref o computed) dice se la pagina corrente accetta rilasci.
 *
 * Gli ascoltatori stanno sul documento e non su un contenitore: appesi a un
 * elemento, la zona finiva dove finiva il contenuto, quindi con una griglia
 * corta — o subito dopo l'ultima riga di tessere — mezza finestra non
 * rispondeva. Da fuori è indistinguibile da un guasto.
 */
export function useZonaDrop(gestisci, abilitata = ref(true)) {
  const dentro = ref(false);
  const attiva = computed(() => Boolean(abilitata.value) && dentro.value && !zonaPrecisa.value);

  /* Non si contano dragenter e dragleave: sul documento arrivano da ogni
   * elemento attraversato, si sbilanciano (un drop fuori dalla finestra non
   * produce il dragleave atteso) e il contatore resta appeso, lasciando
   * l'invito a schermo per sempre. Ci si fida invece di dragover, che scatta
   * di continuo finché il puntatore è dentro: se smette di arrivare, il
   * trascinamento se n'è andato. */
  let orologio = null;
  function respira() {
    dentro.value = true;
    clearTimeout(orologio);
    orologio = setTimeout(() => {
      dentro.value = false;
    }, 250);
  }
  function spegni() {
    clearTimeout(orologio);
    dentro.value = false;
  }

  function alPassaggio(evento) {
    if (!conFile(evento)) return;
    /* Sempre, anche a zona spenta: il default del browser per un drop è
     * NAVIGARE al file, cioè uscire dall'app per mostrare un'immagine. */
    evento.preventDefault();
    if (!abilitata.value || zonaPrecisa.value) {
      evento.dataTransfer.dropEffect = 'none';
      return;
    }
    evento.dataTransfer.dropEffect = 'copy';
    respira();
  }

  function alRilascio(evento) {
    if (!conFile(evento)) return;
    evento.preventDefault();
    spegni();
    // Una zona precisa che ha già gestito il rilascio ferma la propagazione e
    // qui non arriva nulla; il controllo copre il caso in cui ci arrivi.
    if (!abilitata.value || zonaPrecisa.value) return;
    gestisci(evento.dataTransfer);
  }

  onMounted(() => {
    document.addEventListener('dragover', alPassaggio);
    document.addEventListener('drop', alRilascio);
    document.addEventListener('dragend', spegni);
  });
  onBeforeUnmount(() => {
    document.removeEventListener('dragover', alPassaggio);
    document.removeEventListener('drop', alRilascio);
    document.removeEventListener('dragend', spegni);
    clearTimeout(orologio);
  });

  return { attiva };
}
